/**
 * DocumentationStep — 1:1 Haftpflichtgutachten-Layout im A4-Format
 * mit Inline-Editing für Freitextblöcke.
 *
 * Design-Anker: PDF-Vorlage "Haftpflichtgutachten 2605-001" (Auto Gutachter24).
 * Branding: Easy Gutachter Logo + dynamischer User-Name aus Profil.
 *
 * Inline-Editing:
 *   - Freitextblöcke (Auftrag, Besichtigung, Zustand, Schadenhergang …)
 *     sind contentEditable. Auto-Save (debounced 700ms) via PATCH /cases/{id}
 *     mit { documentation: { <key>: <text> } }.
 *   - Strukturierte Felder (Stammdaten, KPIs, Fahrzeugdaten) sind read-only —
 *     werden aus den anderen Steps gespeist.
 *
 * Standardformulierungen werden serverseitig als Default geliefert (siehe
 * DOCUMENTATION_DEFAULTS unten) — sobald der User editiert, überschreibt
 * sein Text den Default.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, apiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Printer, Sparkles, Download, FileText, ExternalLink, Package, FileCheck2, Trash2 } from "lucide-react";
import AbtretungsErklaerung, { calcAbtretungPageCount } from "@/components/AbtretungsErklaerung";
import DATPdfScanModal from "@/components/case/DATPdfScanModal";
import DATContractPullDialog from "@/components/case/DATContractPullDialog";
import {
  DOC_TYPE_LABELS, letterheadLogoUrl, photoUrl, photoLabel,
  fmtEUR, fmtDateLong, fmtDateTime, fmtKM, dash,
} from "./doc/formatters";
import { DEFAULT_TEXTS } from "./doc/defaultTexts";
import EditableText from "./doc/EditableText";
import {
  COLORS, A4Page, SectionTitle, SubTitle, KVList, SummaryRow,
  CostsTable, PositionsTable,
} from "./doc/primitives";
import {
  DamageDiagramReadOnly, DamageList, DamageSelectionSummary,
} from "./doc/damageComponents";
import { PARTS as DAMAGE_PARTS, STATUS as DAMAGE_STATUS } from "@/components/VehicleDamageMap";
import { compressImage } from "@/lib/imageCompression";

// Rollen-Labels für insurance.additional_parties (Kapitel 4 „Weitere Beteiligte")
const PARTY_ROLE_LABELS = {
  zeuge: "Zeuge / Zeugin",
  rechtsanwalt: "Rechtsanwalt",
  halter: "Halter (Geschädigter)",
  fahrer: "Fahrer",
  werkstatt: "Werkstatt",
  polizei: "Polizei",
  gegner_anwalt: "Anwalt der Gegenseite",
  gegner_versicherung: "Sachbearbeiter Gegnerische Vers.",
  mietwagen: "Mietwagenfirma",
  abschleppdienst: "Abschleppdienst",
  sonstige: "Sonstige",
};

// ---------- Main DocumentationStep ----------------------------------------
// Inline-editierbarer EUR-Betrag im A4-Dokument (Klick → Eingabe → Auto-Save)
function EditableAmount({ value, onSave, testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const start = () => {
    setDraft(value ? String(value).replace(".", ",") : "");
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    const s = draft.trim().replace(/€/g, "").replace(/\s/g, "");
    if (s === "") return;
    let n;
    if (s.includes(",")) n = parseFloat(s.replace(/\./g, "").replace(",", "."));
    else n = parseFloat(s);
    if (Number.isFinite(n) && n >= 0 && Math.round(n * 100) / 100 !== Number(value || 0)) {
      onSave(Math.round(n * 100) / 100);
    }
  };
  if (!editing) {
    return (
      <span
        className="doc-edit-amount"
        onClick={start}
        title="Klicken zum Bearbeiten"
        data-testid={testId}
        style={{ cursor: "pointer", borderBottom: "1px dashed #94a3b8", paddingBottom: "1px" }}
      >
        {fmtEUR(value)}
      </span>
    );
  }
  return (
    <input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { setDraft(""); setEditing(false); }
      }}
      placeholder="0,00"
      data-testid={testId ? `${testId}-input` : undefined}
      style={{
        width: "28mm", textAlign: "right", fontSize: "10pt",
        fontVariantNumeric: "tabular-nums", border: "1px solid #0ea5e9",
        borderRadius: "2px", padding: "0.5mm 1mm", outline: "none",
      }}
    />
  );
}

export default function DocumentationStep({ c, accessToken, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [docState, setDocState] = useState(c.documentation || {});
  // Ref hält immer den NEUESTEN Doku-Stand — verhindert, dass ein Save mit
  // veraltetem c.documentation zuvor gespeicherte Blöcke überschreibt.
  const docStateRef = useRef(c.documentation || {});

  // Sync external case-object changes (parent passes updated c after API save)
  useEffect(() => {
    setDocState(c.documentation || {});
    docStateRef.current = c.documentation || {};
  }, [c.documentation]);

  const saveField = useCallback(async (key, val) => {
    const nextDoc = { ...docStateRef.current, [key]: val };
    docStateRef.current = nextDoc;
    setDocState(nextDoc);
    setSaving(true);
    try {
      await api.patch(`/cases/${c.id}`, { documentation: nextDoc });
      onSaved?.({ documentation: nextDoc });
    } catch (e) {
      toast.error(apiError(e, "Speichern fehlgeschlagen"));
    } finally {
      setSaving(false);
    }
  }, [c.id, onSaved]);


  // ---------- Derived Data --------------------------------------------------
  const customer = c.customer || {};
  const vehicle = c.vehicle || {};
  const insurance = c.insurance || {};
  const inspection = c.inspection || {};
  const valuation = c.valuation || {};
  const calc = c.calculation || {};
  const summary = calc.summary || {};
  const ausfall = c.ausfall || {};
  const accounting = c.accounting || {};
  const honorar = c.honorar || {};
  const beg = c.begutachtung || {};

  // Compute defaults that depend on case data
  const orderDateLong = fmtDateLong(c.order_date || c.created_at);
  const docDateLong = fmtDateLong(c.created_at);
  const kalkulationDate = fmtDateLong(c.dat_contract_created_at || c.updated_at);
  // Editor speichert inspection_date/inspection_location/gutachter_name —
  // ältere Akten haben date/location/inspector. Beide Welten normalisieren.
  const inspDateRaw = inspection.inspection_date || inspection.date || "";
  const inspectionDate = inspDateRaw
    ? (String(inspDateRaw).length <= 10 ? fmtDateLong(inspDateRaw) : fmtDateTime(inspDateRaw))
    : "—";
  const inspLocation = inspection.inspection_location
    || [inspection.location, inspection.city].filter(Boolean).join(", ");
  const inspInspector = inspection.gutachter_name || inspection.inspector
    || c.owner?.name || c.owner_name || "";
  const valDate = fmtDateLong(c.updated_at);

  const rawSubtotalNet = Number(summary.subtotal_net || calc.total_net) || 0;
  // Kategorie-Summen: Summary bevorzugt, sonst aus den DAT-Detailpositionen
  // (labour_positions/spare_parts) ableiten — viele DAT-Dossiers liefern
  // SumLabourCosts & Co. nicht mit, nur die Gesamtsumme.
  const labourPos = calc.labour_positions || [];
  const sparePos = calc.spare_parts || [];
  const calcItems = Array.isArray(calc.items) ? calc.items : [];
  const isLacquerPos = (p) => /lacquer|lack/i.test(String(p?.wage_type || ""));
  const sumPos = (list) => Math.round(list.reduce((s, p) => s + Number(p?.total_price || 0), 0) * 100) / 100;
  const sumItems = (cat) => Math.round(calcItems
    .filter((i) => (cat === "surcharge"
      ? !["labor", "parts", "paint"].includes(i?.category)
      : i?.category === cat))
    .reduce((s, i) => s + Number(i?.total_net || 0), 0) * 100) / 100;
  // Roh-DAT-Summary als letzte Quelle (Alt-Akten vor dem Summary-Mapping)
  const datSum = (calc.dat_summary && typeof calc.dat_summary === "object") ? calc.dat_summary : {};
  const laborNet = Number(summary.labor_net || calc.labor_net)
    || sumItems("labor")
    || sumPos(labourPos.filter((p) => !isLacquerPos(p)))
    || Number(datSum.SumLabourCosts) || 0;
  const paintNet = Number(summary.paint_net || calc.paint_net)
    || sumItems("paint")
    || sumPos(labourPos.filter(isLacquerPos))
    || Number(datSum.SumLacquerCosts) || 0;
  const partsNet = Number(summary.parts_net || calc.parts_net)
    || sumItems("parts")
    || sumPos(sparePos)
    || Number(datSum.SumSparePartCosts) || 0;
  let surchargeNet = Number(summary.surcharge_net || calc.surcharge_net)
    || sumItems("surcharge")
    || Number(datSum.SumAuxiliaryCosts) || 0;
  if (!surchargeNet && rawSubtotalNet > 0 && (laborNet || paintNet || partsNet)) {
    const residual = Math.round((rawSubtotalNet - laborNet - paintNet - partsNet) * 100) / 100;
    if (residual > 0.01) surchargeNet = residual;
  }
  // Reparaturkosten-Summe: explizite Summary/DAT-Totale bevorzugt, sonst aus
  // den Kategoriesummen ableiten (Akten mit genullter Summary aber vorhandenen
  // DAT-Positionen zeigten sonst 0,00 EUR).
  const repairCostsNet = rawSubtotalNet
    || Math.round((laborNet + paintNet + partsNet + surchargeNet) * 100) / 100;
  const calcTaxRate = Number(summary.tax_rate) || 19;
  const repairCostsGross = Number(summary.total_gross || calc.total_gross)
    || Math.round(repairCostsNet * (1 + calcTaxRate / 100) * 100) / 100;

  // Kapitel 9: manuell eingetragene Kosten-Summen → calculation.summary.
  // Beim ersten Edit werden alle 4 angezeigten (ggf. abgeleiteten) Werte
  // persistiert, damit die Summary ab dann explizit und konsistent ist.
  const saveSummaryField = async (field, val) => {
    const persisted = ((c.calculation || {}).summary || {});
    const displayed = {
      labor_net: laborNet, parts_net: partsNet,
      paint_net: paintNet, surcharge_net: surchargeNet,
    };
    const s = { ...persisted, ...displayed, [field]: val };
    const cats = ["labor_net", "parts_net", "paint_net", "surcharge_net"];
    const catSum = Math.round(cats.reduce((t, k) => t + Number(s[k] || 0), 0) * 100) / 100;
    const prevCatSum = Math.round(cats.reduce((t, k) => t + Number(displayed[k] || 0), 0) * 100) / 100;
    // Reparaturkosten-Summe mitziehen, wenn sie leer ist oder bisher der
    // Kategorien-Summe entsprach; bei direktem Edit gilt der eingegebene Wert.
    if (cats.includes(field) && (!Number(s.subtotal_net || 0) || Number(s.subtotal_net) === prevCatSum)) {
      s.subtotal_net = catSum;
    }
    if (Number(s.subtotal_net || 0) !== Number(persisted.subtotal_net || 0)) {
      const rate = Number(s.tax_rate || 19);
      s.tax_rate = rate;
      s.tax_amount = Math.round(Number(s.subtotal_net || 0) * rate) / 100;
      s.total_gross = Math.round((Number(s.subtotal_net || 0) + s.tax_amount) * 100) / 100;
    }
    setSaving(true);
    try {
      await api.patch(`/cases/${c.id}`, { calculation: { summary: s } });
      onSaved?.({ calculation: { ...(c.calculation || {}), summary: s } });
    } catch (e) {
      toast.error(apiError(e, "Betrag konnte nicht gespeichert werden"));
    } finally {
      setSaving(false);
    }
  };
  const wbwGross = Number(valuation.replacement_value) || 0;
  const restwert = Number(valuation.residual_value) || 0;
  const minderwert = Number(valuation.market_depreciation || valuation.depreciation) || 0;
  const repairDays = ausfall.repair_days || ausfall.repair_duration_days || "—";
  const recoveryDays = ausfall.recovery_days || ausfall.replacement_days || valuation.wiederbeschaffungsdauer || "—";
  const dailyRate = Number(ausfall.daily_rate || accounting.daily_rate || accounting.daily_loss_of_use || 0);
  const rentalGroup = ausfall.rental_class || ausfall.group || ausfall.rental_category
    || accounting.rental_group || accounting.loss_of_use_class || "—";
  // Summen: Step speichert daily_rate × repair_duration_days nicht — hier ableiten.
  const naDays = Number(ausfall.repair_duration_days || ausfall.repair_days || 0);
  const naTotal = Number(ausfall.total_nutzungsausfall)
    || Math.round(dailyRate * naDays * 100) / 100;
  const rentalTotal = Number(ausfall.total_mietwagen)
    || Math.round(Number(ausfall.rental_daily_rate || 0) * Number(ausfall.rental_days_count || 0) * 100) / 100;

  // Honorar-Felder (BVSK)
  const honorarBase = Number(honorar.base_fee) || 0;
  const honorarTravelKm = Number(honorar.travel_km) || Number(inspection.distance_km) || 0;
  const honorarTravelRate = Number(honorar.travel_rate_per_km) || 0.70;
  const honorarTravelTotal = honorarTravelKm * honorarTravelRate;
  const honorarPhoto = Number(honorar.photo_fee) || 0;
  const honorarWriting = Number(honorar.writing_fee) || 0;
  const honorarPostage = Number(honorar.postage_fee) || 0;
  const honorarSubtotal = Number(honorar.subtotal_net) || (honorarBase + honorarTravelTotal + honorarPhoto + honorarWriting + honorarPostage);
  const honorarTaxRate = Number(honorar.tax_rate) || 19;
  const honorarTax = Number(honorar.tax_amount) || (honorarSubtotal * honorarTaxRate / 100);
  const honorarGross = Number(honorar.total_gross) || (honorarSubtotal + honorarTax);
  // HonorarStep speichert Nebenkosten als Item-Listen (BVSK) — die Doku
  // rendert diese bevorzugt; die Einzelfelder oben sind nur Alt-Fallback.
  const honorarNkItems = [
    ...(Array.isArray(honorar.nebenkosten_items) ? honorar.nebenkosten_items : []),
    ...(Array.isArray(honorar.optionale_nebenkosten_items)
      ? honorar.optionale_nebenkosten_items.filter((i) => (honorar.optional_keys || []).includes(i.key))
      : []),
  ].filter((i) => i && (Number(i.amount) || 0) !== 0);

  // Calculation items gruppiert nach Kategorie (für Detail-Tabelle Seite 5)
  const itemsByCategory = {
    labor: calcItems.filter((it) => it.category === "labor"),
    parts: calcItems.filter((it) => it.category === "parts"),
    paint: calcItems.filter((it) => it.category === "paint"),
    surcharge: calcItems.filter((it) => it.category === "surcharge"),
  };

  const doc = {
    caseRef: c.case_reference || c.case_number || c.id?.slice(0, 8),
    schadenNr: insurance.claim_number || "",
    licensePlate: vehicle.license_plate || "",
    gutachterName: c.owner?.name || c.owner_name || "",
    gutachterCompany: c.owner?.company || c.owner_company || "",
    gutachterAddress: [
      c.owner?.address_line,
      [c.owner?.postal_code, c.owner?.city].filter(Boolean).join(" "),
    ].filter(Boolean).join(", ") || c.owner_address || "",
    iban: c.owner?.iban || "",
    bic: c.owner?.bic || "",
    bankName: c.owner?.bank_name || "",
    vatId: c.owner?.vat_id || c.owner?.tax_number || "",
    phone: c.owner?.phone || c.owner?.mobile || "",
    email: c.owner?.email || "",
    letterheadUrl: c.owner?.letterhead_logo_path
      ? letterheadLogoUrl(c.owner.letterhead_logo_path, undefined, accessToken)
      : null,
  };

  // Lichtbilder — 6 Fotos pro Seite (2x3 Grid), max so viele Seiten wie nötig
  const photos = Array.isArray(c.photos)
    ? [...c.photos].filter((p) => p?.in_documentation !== false).sort((a, b) => (a.order || 0) - (b.order || 0))
    : [];
  const PHOTOS_PER_PAGE = 6;
  const photoPages = photos.length > 0 ? Math.ceil(photos.length / PHOTOS_PER_PAGE) : 0;
  // 13 feste Standard-Seiten (Titel, Zusammenfassung, Fahrzeugbeschreibung,
  // Auftrag+Versicherung, Besichtigung+Zustand, Schadenhergang+Schadenfeststellung,
  // Vorschäden-Diagramm, Instandsetzung, Wertermittlung, Wirtschaftlichkeit,
  // Nutzungsausfall, Sachverständigen-Honorar, Schlusswort)
  // + abtretungPages (dynamisch je nach Klausel-Anzahl)
  // + photoPages Foto-Seiten + 1 Anlagen-Seite
  const STANDARD_PAGES_BEFORE_ABTRETUNG = 13;
  const abtretungPages = calcAbtretungPageCount(c);
  const ABTRETUNG_PAGE = STANDARD_PAGES_BEFORE_ABTRETUNG + 1;
  const TOTAL_PAGES = STANDARD_PAGES_BEFORE_ABTRETUNG + abtretungPages + photoPages + 1;
  const FIRST_PHOTO_PAGE = STANDARD_PAGES_BEFORE_ABTRETUNG + abtretungPages + 1;
  const APPENDIX_PAGE = TOTAL_PAGES;

  // Defaults bound to case data
  const defaults = useMemo(() => ({
    auftrag_text: DEFAULT_TEXTS.auftrag_text({ orderDate: orderDateLong }),
    besichtigung_text: DEFAULT_TEXTS.besichtigung_text(),
    // Begutachtungs-Step-Daten fließen als Default in die Doku-Texte —
    // manuelle Doku-Edits (docState) haben weiterhin Vorrang.
    zustand_text: beg.zustandsbeschreibung || DEFAULT_TEXTS.zustand_text(),
    schadenhergang_text: beg.detaillierte_schilderung || beg.schilderung || DEFAULT_TEXTS.schadenhergang_text(),
    schadenfeststellung_text: DEFAULT_TEXTS.schadenfeststellung_text(),
    plausibilitaet_text: beg.plausibilitaet || DEFAULT_TEXTS.plausibilitaet_text(),
    vorschaeden_text: DEFAULT_TEXTS.vorschaeden_text(),
    reparaturauftrag_text: DEFAULT_TEXTS.reparaturauftrag_text(),
    reparaturkosten_intro_text: DEFAULT_TEXTS.reparaturkosten_intro_text({ kalkulationsdatum: kalkulationDate }),
    wertveraenderung_text: DEFAULT_TEXTS.wertveraenderung_text(),
    wiederbeschaffungswert_text: DEFAULT_TEXTS.wiederbeschaffungswert_text({ stichtag: valDate }),
    restwert_text: DEFAULT_TEXTS.restwert_text(),
    fahrzeugausfall_text: DEFAULT_TEXTS.fahrzeugausfall_text(),
    reparaturdauer_text: DEFAULT_TEXTS.reparaturdauer_text({ dauer: repairDays }),
    wiederbeschaffungsdauer_text: DEFAULT_TEXTS.wiederbeschaffungsdauer_text({ tage: recoveryDays }),
    nutzungsausfall_text: DEFAULT_TEXTS.nutzungsausfall_text({
      rate: fmtEUR(dailyRate),
      gruppe: rentalGroup,
      klasse: rentalGroup,
    }),
    schlusswort_text: DEFAULT_TEXTS.schlusswort_text(),
  }), [orderDateLong, kalkulationDate, valDate, repairDays, recoveryDays, dailyRate, rentalGroup,
       beg.zustandsbeschreibung, beg.detaillierte_schilderung, beg.schilderung, beg.plausibilitaet]);

  const ed = (key) => (
    <EditableText
      value={docState[key]}
      defaultText={defaults[key]}
      onSave={(val) => saveField(key, val)}
    />
  );

  // ---------- DAT-PDF-Scan-Integration -------------------------------------
  // Erlaubt dem Gutachter, eine fertige DAT-Kalkulations-PDF direkt aus der
  // Dokumentation heraus zu scannen. Die extrahierten Werte (Fahrzeug,
  // Kosten, Positionen) wandern via PATCH /cases/{id} in die Akte und werden
  // automatisch in allen Sektionen der Doku angezeigt.
  const [datScanOpen, setDatScanOpen] = useState(false);
  const [datScanFile, setDatScanFile] = useState(null);
  const [datDragActive, setDatDragActive] = useState(false);
  const datDragDepth = useRef(0);
  // ---------- DAT-Vorgangsnummer-Pull (Live-Abruf via SOAP) ----------------
  // Alternative zum PDF-Scan: User gibt eine DAT-Vorgangsnummer (Contract-ID)
  // ein, wir holen die Daten live via SOAP getContractList und mergen sie
  // in die Akte. Funktioniert auch für extern angelegte DAT-Vorgänge.
  const [datPullOpen, setDatPullOpen] = useState(false);

  // ---------- DAT-Kalkulations-PDFs (Anhänge) ------------------------------
  // Nach erfolgreichem PDF-Scan oder DAT-Pull-Back hängen wir die Original-
  // PDF in `case.documents` mit doc_type="dat_calculation". Hier extrahieren
  // wir die Liste und bauen die Download-URLs (Auth-Token im Query-Param,
  // damit `<a target="_blank">` ohne Header funktioniert).
  const datDocuments = useMemo(() => (
    (c.documents || []).filter((d) => d?.doc_type === "dat_calculation")
  ), [c.documents]);
  const buildDocUrl = (storagePath) => {
    if (!storagePath) return "#";
    const backendBase = (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "";
    const token = encodeURIComponent(accessToken || "");
    return `${backendBase}/api/files/download/${encodeURI(storagePath)}?auth=${token}`;
  };

  // ---------- PDF-Export (100% serverseitig) --------------------------------
  // Browser-Rendering (html2canvas) ist komplett abgeschafft — es fror die
  // Seite ein und crashte auf Geräten mit wenig Speicher. Der Server rendert
  // das Gutachten via reportlab; hier nur: Job starten → pollen → downloaden.
  const [exporting, setExporting] = useState(false);
  const [bundling, setBundling] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);

  const runServerPdfJob = useCallback(async ({ includeGutachten, documentIds, mode }) => {
    const payload = {
      include_gutachten: includeGutachten,
      document_ids: documentIds,
      mode,
    };
    let startResp;
    try {
      ({ data: startResp } = await api.post(`/cases/${c.id}/bundle`, payload));
    } catch (err) {
      if (err?.response?.status === 409) {
        // Ein anderer Job läuft gerade (z.B. Vorab-Erstellung mit anderer
        // Auswahl) → warten bis er fertig ist, dann unseren starten.
        setExportProgress("Warte auf laufende PDF-Erstellung…");
        const tw = Date.now();
        while (Date.now() - tw < 600_000) {
          await new Promise((r) => setTimeout(r, 3000));
          try {
            const { data } = await api.get(`/cases/${c.id}/bundle/status`);
            if (data.status !== "running") break;
          } catch { continue; }
        }
        ({ data: startResp } = await api.post(`/cases/${c.id}/bundle`, payload));
      } else {
        throw err;
      }
    }

    let status = null;
    if (startResp.status === "done") {
      // Cache-Treffer (z.B. durch Vorab-Erstellung beim Öffnen des Tabs)
      // → PDF liegt schon fertig auf dem Server, sofort herunterladen.
      const { data } = await api.get(`/cases/${c.id}/bundle/status`);
      status = data;
    } else {
      // Fertig-Signal kommt per WebSocket-Push (wie bei der Konkurrenz);
      // Polling läuft nur noch als Fallback (10s bei offenem WS, sonst 2s).
      setExportProgress("Server erstellt PDF…");
      status = await new Promise((resolve) => {
        let settled = false;
        let wsOpen = false;
        let ws = null;
        let pollTimer = null;
        let last = null;
        const t0 = Date.now();
        const finish = (val) => {
          if (settled) return;
          settled = true;
          if (pollTimer) clearTimeout(pollTimer);
          try { ws?.close(); } catch (_) { /* ignore */ }
          resolve(val);
        };
        const showProgress = (progress) => {
          const secs = Math.round((Date.now() - t0) / 1000);
          const timeStr = secs >= 90 ? `${Math.floor(secs / 60)}m ${secs % 60}s` : `${secs}s`;
          setExportProgress(progress ? `${progress} (${timeStr})` : `Server erstellt PDF… ${timeStr}`);
        };
        // Finaler Status kommt immer per GET (liefert documents/failed_docs)
        const fetchFinal = async () => {
          try {
            const { data } = await api.get(`/cases/${c.id}/bundle/status`);
            finish(data);
          } catch (_) { /* Polling-Fallback übernimmt */ }
        };
        try {
          const proto = BACKEND_URL.startsWith("https") ? "wss" : "ws";
          const host = BACKEND_URL.replace(/^https?:\/\//, "");
          ws = new WebSocket(`${proto}://${host}/api/ws/bundle/${c.id}?token=${encodeURIComponent(accessToken || "")}`);
          ws.onopen = () => { wsOpen = true; };
          ws.onclose = () => { wsOpen = false; };
          ws.onerror = () => { wsOpen = false; };
          ws.onmessage = (msg) => {
            try {
              const evt = JSON.parse(msg.data);
              if (evt.status === "done" || evt.status === "error") {
                fetchFinal();
              } else if (evt.status === "running") {
                last = evt;
                showProgress(evt.progress);
              }
            } catch (_) { /* ignore */ }
          };
        } catch (_) { /* kein WebSocket verfügbar → reines Polling */ }
        const poll = async () => {
          if (settled) return;
          // Großzügiges Fenster (15 Min): in Produktion kann der allererste
          // Durchlauf einer Akte wegen kaltem Foto-Speicher Minuten dauern.
          if (Date.now() - t0 > 900_000) return finish(last);
          try {
            const { data } = await api.get(`/cases/${c.id}/bundle/status`);
            last = data;
            if (data.status === "done" || data.status === "error") return finish(data);
            showProgress(data.progress);
          } catch (_) { /* retry */ }
          pollTimer = setTimeout(poll, wsOpen ? 10000 : 2000);
        };
        pollTimer = setTimeout(poll, 2000);
      });
    }
    if (!status || status.status === "error") {
      throw new Error(status?.error || "Unbekannter Fehler bei der Erstellung");
    }
    if (status.status !== "done") {
      throw new Error("Der Server braucht ungewöhnlich lange. Die PDF wird im Hintergrund fertiggestellt und erscheint anschließend in der Anhänge-Liste unter \"Gebündelt\" — bitte dort herunterladen.");
    }
    // Direkter Server-Download-Link statt Blob: der Browser lädt nativ über
    // Content-Disposition:attachment — programmatische Blob-Downloads werden
    // von manchen Browsern blockiert, wenn sie (wie hier nach dem Polling)
    // lange nach dem User-Klick ausgelöst werden.
    const backendBase = (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "";
    const dlUrl = `${backendBase}/api/cases/${c.id}/bundle/download?auth=${encodeURIComponent(accessToken || "")}`;
    const filename = status.filename
      || `Gutachten_${c.case_reference || c.case_number || c.id?.slice(0, 8)}.pdf`;
    setExportProgress("Download…");
    window.location.assign(dlUrl);
    return { status, dlUrl, filename };
  }, [c.id, c.case_reference, c.case_number, accessToken]);

  const exportPdf = useCallback(async () => {
    setExporting(true);
    setExportProgress(null);
    try {
      const { dlUrl, filename } = await runServerPdfJob({
        includeGutachten: true, documentIds: [], mode: "gutachten",
      });
      toast.success(`${filename} wird heruntergeladen`, {
        duration: 20000,
        action: {
          label: "Erneut herunterladen",
          onClick: () => window.location.assign(dlUrl),
        },
      });
    } catch (e) {
      toast.error("PDF-Erstellung fehlgeschlagen: " + (e?.response?.data?.detail || e?.message || e));
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  }, [runServerPdfJob]);

  // ─── Bundle-Export ────────────────────────────────────────────────────
  // Auswahl welche Anhänge zusätzlich zum Haftpflichtgutachten gemergt werden.
  // Default: alles ist angekreuzt — User kann gezielt abwählen, was er NICHT
  // an die Versicherung schicken will (z.B. interne Notizen).
  const [bundleSelection, setBundleSelection] = useState({
    gutachten: true,
    // Map von Document-ID → boolean. Auto-aktiviert wenn case.documents wächst.
    docs: {},
  });
  // Default-State synchronisieren wenn neue Akten-Dokumente reinkommen
  useEffect(() => {
    setBundleSelection((prev) => {
      const next = { ...prev.docs };
      let changed = false;
      for (const d of c.documents || []) {
        const key = d.id || d.storage_path;
        if (!key) continue;
        // Fertige Bundles sind keine wählbaren Anhänge (Rekursions-Schutz)
        if (d.doc_type === "gutachten_bundle") continue;
        if (next[key] === undefined) {
          // Dokument neu: standardmäßig drin
          next[key] = true;
          changed = true;
        }
      }
      return changed ? { ...prev, docs: next } : prev;
    });
  }, [c.documents]);

  const toggleBundleDoc = (key) =>
    setBundleSelection((prev) => ({ ...prev, docs: { ...prev.docs, [key]: !prev.docs[key] } }));

  // Bundle VORAB erstellen sobald der Dokumentations-Tab geöffnet wird
  // (Standard-Auswahl: Gutachten + alle Anhänge). Beim Klick auf "Bundle als
  // PDF" ist die Datei dann meist schon fertig → sofortiger Download (Cache).
  const prewarmedRef = useRef(false);
  useEffect(() => {
    if (prewarmedRef.current || !c.id) return;
    prewarmedRef.current = true;
    const document_ids = (c.documents || [])
      .filter((d) => d.doc_type !== "gutachten_bundle")
      .map((d) => d.id || d.storage_path)
      .filter(Boolean);
    api.post(`/cases/${c.id}/bundle`, {
      include_gutachten: true,
      document_ids,
      mode: "bundle",
      prewarm: true,
    }).catch(() => {});
  }, [c.id, c.documents]);

  // Inline-Label-Edit pro Dokument. Wenn editingDocId gesetzt ist, zeigt
  // die Sidebar ein <input> statt des statischen Labels.
  const [editingDocId, setEditingDocId] = useState(null);
  const [editingDocLabel, setEditingDocLabel] = useState("");
  const saveDocLabel = useCallback(async (docId) => {
    const newLabel = editingDocLabel.trim();
    setEditingDocId(null);
    if (!docId) return;
    try {
      await api.patch(`/files/cases/${c.id}/documents/${docId}`, { label: newLabel });
      // Parent-State optimistisch updaten so dass Re-Render funktioniert
      if (typeof onSaved === "function") {
        onSaved({
          documents: (c.documents || []).map((d) =>
            d.id === docId ? { ...d, label: newLabel } : d
          ),
        });
      }
      toast.success("Beschriftung gespeichert");
    } catch (e) {
      toast.error(apiError(e, "Beschriftung konnte nicht gespeichert werden"));
    }
  }, [editingDocLabel, c.id, c.documents, onSaved]);

  // Upload weiterer Dokumente direkt aus der Sidebar (z.B. Lichtbilder,
  // Polizeibericht, Schadenanzeige). Erlaubt PDF/JPG/PNG.
  const bundleFileInputRef = useRef(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const onDeleteDoc = useCallback(async (doc) => {
    const name = doc.label || doc.original_filename || "Dokument";
    if (!window.confirm(`„${name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/files/cases/${c.id}/documents/${doc.id}`);
      toast.success(`„${name}" gelöscht`);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e, "Löschen fehlgeschlagen"));
    }
  }, [c.id, onSaved]);

  const onBundleUpload = useCallback(async (filesList) => {
    if (!filesList || filesList.length === 0) return;
    setUploadingDoc(true);
    // Robust gegen Proxy-Limits in Produktion: Bilder client-seitig
    // komprimieren, jede Datei EINZELN hochladen (kleine Requests) und
    // pro Datei 1x automatisch neu versuchen. Fehler nennen die Datei.
    const okDocs = [];
    const failed = [];
    for (const f of Array.from(filesList)) {
      try {
        let payload = f;
        if (/^image\//i.test(f.type || "")) {
          try { payload = await compressImage(f); } catch { payload = f; }
        }
        const fd = new FormData();
        fd.append("files", payload, payload.name || f.name || "dokument");
        fd.append("doc_type", "other");
        let lastErr = null;
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            const { data } = await api.post(`/files/cases/${c.id}/documents`, fd, { timeout: 90_000 });
            okDocs.push(...(data?.documents || []));
            lastErr = null;
            break;
          } catch (err) {
            lastErr = err;
            const st = err?.response?.status;
            if (st && st >= 400 && st < 500 && st !== 408 && st !== 429) break;
          }
        }
        if (lastErr) throw lastErr;
      } catch (e) {
        const st = e?.response?.status;
        const reason = st === 413 ? "Datei zu groß" : st ? `Fehler ${st}` : "Netzwerkfehler";
        failed.push(`${f.name || "Datei"} (${reason})`);
      }
    }
    if (okDocs.length > 0 && typeof onSaved === "function") {
      onSaved({ documents: [...(c.documents || []), ...okDocs] });
    }
    if (okDocs.length > 0) {
      toast.success(`${okDocs.length} Dokument${okDocs.length === 1 ? "" : "e"} hochgeladen`);
    }
    if (failed.length > 0) {
      toast.error(`Nicht hochgeladen: ${failed.join(", ")}`);
    }
    setUploadingDoc(false);
    if (bundleFileInputRef.current) bundleFileInputRef.current.value = "";
  }, [c.id, c.documents, onSaved]);

  // Bundle wird KOMPLETT SERVERSEITIG erstellt (reportlab + pypdf) — das
  // frühere html2canvas-Rendering im Browser scheiterte wiederholt an
  // Gerätespeicher-Limits (leere Seiten, minutenlange Exporte). Ablauf:
  // POST startet Hintergrund-Job → Status-Polling → Blob-Download.
  const exportBundle = useCallback(async () => {
    setBundling(true);
    setExportProgress(null);
    try {
      const document_ids = (c.documents || [])
        .filter((d) => d.doc_type !== "gutachten_bundle")
        .map((d) => d.id || d.storage_path)
        .filter((key) => key && bundleSelection.docs[key]);

      const { status, dlUrl, filename } = await runServerPdfJob({
        includeGutachten: bundleSelection.gutachten,
        documentIds: document_ids,
        mode: "bundle",
      });

      // Neu abgelegtes Bundle-Dokument in die lokale Case-Kopie übernehmen,
      // damit es sofort in der Liste ("Gebündelt") erscheint.
      if (Array.isArray(status.documents)) {
        onSaved?.({ documents: status.documents });
      }

      if (status.failed_docs?.length > 0) {
        toast.warning(`Bundle erstellt, aber folgende Anhänge fehlen: ${status.failed_docs.join(", ")}`);
      } else {
        toast.success(`${filename} wird heruntergeladen`, {
          duration: 20000,
          action: {
            label: "Erneut herunterladen",
            onClick: () => window.location.assign(dlUrl),
          },
        });
      }
    } catch (e) {
      toast.error("Bundle-Export fehlgeschlagen: " + (e?.response?.data?.detail || e?.message || e));
    } finally {
      setBundling(false);
      setExportProgress(null);
    }
  }, [bundleSelection, c.documents, runServerPdfJob, onSaved]);

  // ---------- Render --------------------------------------------------------
  return (
    <div
      data-testid="documentation-step"
      className="relative"
      onDragEnter={(e) => {
        if (!e.dataTransfer?.types?.includes("Files")) return;
        datDragDepth.current += 1;
        setDatDragActive(true);
      }}
      onDragOver={(e) => {
        if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
      }}
      onDragLeave={() => {
        datDragDepth.current = Math.max(0, datDragDepth.current - 1);
        if (datDragDepth.current === 0) setDatDragActive(false);
      }}
      onDrop={(e) => {
        if (!e.dataTransfer?.types?.includes("Files")) return;
        e.preventDefault();
        datDragDepth.current = 0;
        setDatDragActive(false);
        const f = e.dataTransfer.files?.[0];
        if (!f) return;
        if (!f.name.toLowerCase().endsWith(".pdf") && f.type !== "application/pdf") {
          toast.error("Bitte eine DAT-Kalkulations-PDF ablegen");
          return;
        }
        setDatScanFile(f);
        setDatScanOpen(true);
      }}
    >
      {/* Drag&Drop-Overlay: PDF irgendwo auf die Dokumentation ziehen → DAT-Scan */}
      {datDragActive && (
        <div
          className="absolute inset-0 z-40 flex items-center justify-center rounded-lg border-4 border-dashed border-cyan-500 bg-cyan-50/80 pointer-events-none"
          data-testid="doc-dat-drop-overlay"
        >
          <div className="text-center">
            <Sparkles className="h-10 w-10 mx-auto text-cyan-600" />
            <div className="mt-2 text-lg font-semibold text-cyan-900">DAT-PDF hier ablegen</div>
            <div className="text-sm text-cyan-700">Scanner startet automatisch</div>
          </div>
        </div>
      )}
      {/* Toolbar — minimalistisch */}
      <div className="sticky top-0 z-20 mb-4 flex items-center justify-between gap-3 px-4 py-2.5 bg-white/95 backdrop-blur-sm border border-zinc-200 rounded-lg shadow-sm">
        <div className="flex items-center gap-3">
          <div className="text-sm font-semibold text-zinc-900">Dokumentation</div>
          <div className="text-[11px] text-zinc-500">A4 · klicke in einen Textblock zum Bearbeiten · Auto-Save</div>
        </div>
        <div className="flex items-center gap-2">
          {saving && (
            <span className="text-[11px] text-zinc-500 inline-flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> speichern…
            </span>
          )}
          <button
            type="button"
            onClick={() => setDatPullOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-cyan-300 bg-cyan-50/50 text-cyan-700 rounded-md hover:bg-cyan-100 transition-colors"
            data-testid="doc-dat-pull-btn"
            title="DAT-Vorgangsnummer eingeben — Werte werden live aus DAT geholt und in die Dokumentation übernommen"
          >
            <Download className="h-3.5 w-3.5" /> Aus DAT-Vorgang
          </button>
          <button
            type="button"
            onClick={() => setDatScanOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-teal-300 bg-teal-50/50 text-teal-700 rounded-md hover:bg-teal-100 transition-colors"
            data-testid="doc-dat-scan-btn"
            title="Fertige DAT-Kalkulations-PDF hochladen — Werte werden automatisch in die Dokumentation übernommen"
          >
            <Sparkles className="h-3.5 w-3.5" /> DAT-PDF scannen
          </button>
          {datDocuments.length > 0 && (
            <a
              href={buildDocUrl(datDocuments[datDocuments.length - 1].storage_path)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-amber-300 bg-amber-50/60 text-amber-800 rounded-md hover:bg-amber-100 transition-colors"
              data-testid="doc-dat-pdf-open-btn"
              title="Die zuletzt angehängte DAT-Kalkulations-PDF in einem neuen Tab öffnen"
            >
              <FileText className="h-3.5 w-3.5" />
              DAT-PDF öffnen
              {datDocuments.length > 1 && (
                <span className="text-[10px] text-amber-700/80">({datDocuments.length})</span>
              )}
            </a>
          )}
          <button
            type="button"
            onClick={exportPdf}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-zinc-300 rounded-md hover:bg-zinc-50 transition-colors disabled:opacity-60 disabled:cursor-wait"
            data-testid="doc-print-btn"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Printer className="h-3.5 w-3.5" />}
            {exporting ? (exportProgress || "PDF wird erstellt …") : "PDF herunterladen"}
          </button>
        </div>
      </div>

      <div className="flex gap-4 items-start">
        {/* ───── Bundle-Sidebar: Auswahl welche Dokumente zum
             Versicherungs-Bundle gehören. Zwischen Step-Sidebar und A4-
             Vorschau. Sticky damit sie beim Scrollen sichtbar bleibt. ─── */}
        <aside
          className="no-print sticky top-[64px] w-72 shrink-0 bg-white border border-zinc-200 rounded-lg shadow-sm overflow-hidden flex flex-col"
          style={{ maxHeight: "calc(100vh - 88px)" }}
          data-testid="doc-bundle-sidebar"
        >
          <div className="px-3 py-2.5 bg-gradient-to-br from-cyan-50 to-sky-50 border-b border-cyan-200 shrink-0">
            <div className="flex items-center gap-1.5 text-[11px] font-bold text-cyan-900 uppercase tracking-wider">
              <Package className="h-3.5 w-3.5" /> Versicherungs-Bundle
            </div>
            <div className="text-[10px] text-cyan-700 mt-0.5 leading-tight">
              Auswählen, beschriften &amp; zu einer fertigen PDF bündeln
            </div>
          </div>

          <div className="flex-1 p-2 space-y-1 overflow-y-auto min-h-0">
            {/* Haftpflichtgutachten (immer da, Default an) */}
            <label
              className="flex items-start gap-2 p-2 rounded-md hover:bg-zinc-50 cursor-pointer group"
              data-testid="bundle-item-gutachten"
            >
              <input
                type="checkbox"
                checked={bundleSelection.gutachten}
                onChange={() => setBundleSelection((p) => ({ ...p, gutachten: !p.gutachten }))}
                className="mt-0.5 h-4 w-4 accent-cyan-600 shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 text-[11.5px] font-semibold text-zinc-900">
                  <FileCheck2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                  <span className="truncate">Haftpflichtgutachten</span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5">Aktueller A4-Entwurf</div>
              </div>
            </label>

            {/* Akten-Anhänge mit Inline-Edit und Beschriftungen */}
            {(c.documents || []).length === 0 && (
              <div className="px-2 py-3 text-[10.5px] text-zinc-400 italic text-center">
                Noch keine Anhänge — unten hinzufügen
              </div>
            )}
            {(c.documents || []).map((doc) => {
              const key = doc.id || doc.storage_path;
              const checked = !!bundleSelection.docs[key];
              const displayLabel = doc.label || doc.original_filename || doc.filename || "Anhang.pdf";
              const typeBadge = DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type || "Dokument";
              const isEditing = editingDocId === doc.id;
              // Fertiges Bundle: kein Anhang zum Auswählen, sondern dauerhaft
              // gespeichertes Ergebnis — jederzeit erneut herunterladbar.
              if (doc.doc_type === "gutachten_bundle") {
                return (
                  <a
                    key={key}
                    href={buildDocUrl(doc.storage_path)}
                    className="flex items-start gap-2 p-2 rounded-md bg-emerald-50/70 border border-emerald-200 hover:bg-emerald-100/70 transition-colors"
                    data-testid="bundle-item-gutachten-bundle"
                  >
                    <Download className="mt-0.5 h-4 w-4 text-emerald-700 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11.5px] font-medium text-emerald-900 truncate">{displayLabel}</div>
                      <div className="text-[10px] text-emerald-700/80 mt-0.5">
                        Gebündelt{doc.created_at ? ` · ${new Date(doc.created_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""} · erneut herunterladen
                      </div>
                    </div>
                  </a>
                );
              }
              return (
                <div
                  key={key}
                  className="flex items-start gap-2 p-2 rounded-md hover:bg-zinc-50 group"
                  data-testid={`bundle-item-${doc.doc_type || 'other'}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleBundleDoc(key)}
                    className="mt-0.5 h-4 w-4 accent-cyan-600 shrink-0 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editingDocLabel}
                        onChange={(e) => setEditingDocLabel(e.target.value)}
                        onBlur={() => saveDocLabel(doc.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); saveDocLabel(doc.id); }
                          if (e.key === "Escape") { e.preventDefault(); setEditingDocId(null); }
                        }}
                        autoFocus
                        maxLength={120}
                        className="w-full text-[11.5px] font-medium text-zinc-900 px-1.5 py-0.5 border border-cyan-400 rounded outline-none focus:ring-1 focus:ring-cyan-500"
                        data-testid={`bundle-label-input-${doc.id}`}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingDocId(doc.id);
                          setEditingDocLabel(doc.label || "");
                        }}
                        className="w-full text-left text-[11.5px] font-medium text-zinc-900 truncate hover:text-cyan-700 cursor-text"
                        title="Klicken zum Beschriften"
                        data-testid={`bundle-label-${doc.doc_type || 'other'}`}
                      >
                        {displayLabel}
                      </button>
                    )}
                    <div className="text-[10px] text-zinc-500 flex items-center gap-1.5 mt-0.5">
                      <span>{typeBadge}</span>
                      {doc.original_filename && doc.label && (
                        <span className="text-zinc-400 truncate" title={doc.original_filename}>
                          · {doc.original_filename}
                        </span>
                      )}
                    </div>
                  </div>
                  <a
                    href={buildDocUrl(doc.storage_path)}
                    className="mt-0.5 p-1 rounded text-zinc-400 hover:text-cyan-700 hover:bg-cyan-50 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity"
                    title="Dokument herunterladen"
                    data-testid={`bundle-download-${doc.doc_type || 'other'}`}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </a>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDeleteDoc(doc); }}
                    className="mt-0.5 p-1 rounded text-zinc-400 hover:text-red-600 hover:bg-red-50 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity"
                    title="Dokument löschen"
                    data-testid={`bundle-delete-${doc.doc_type || 'other'}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}

            {/* Inline-Upload-Slot */}
            <button
              type="button"
              onClick={() => bundleFileInputRef.current?.click()}
              disabled={uploadingDoc}
              className="w-full mt-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 text-[11px] font-medium text-zinc-700 border-2 border-dashed border-zinc-300 rounded-md hover:border-cyan-400 hover:bg-cyan-50/40 hover:text-cyan-700 transition-colors disabled:opacity-60 disabled:cursor-wait"
              data-testid="bundle-upload-btn"
            >
              {uploadingDoc ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> wird hochgeladen…
                </>
              ) : (
                <>
                  <Download className="h-3.5 w-3.5 rotate-180" />
                  Weiteres Dokument hochladen
                </>
              )}
            </button>
            <input
              ref={bundleFileInputRef}
              type="file"
              multiple
              accept="application/pdf,image/png,image/jpeg,image/jpg"
              onChange={(e) => onBundleUpload(e.target.files)}
              className="hidden"
              data-testid="bundle-upload-input"
            />

            {/* Alle Dokumente gesammelt als ZIP herunterladen */}
            {(c.documents || []).length > 0 && (
              <a
                href={`${BACKEND_URL}/api/files/cases/${c.id}/documents.zip?auth=${encodeURIComponent(accessToken || "")}`}
                className="w-full mt-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 text-[11px] font-semibold text-cyan-800 bg-cyan-50 border border-cyan-200 rounded-md hover:bg-cyan-100 transition-colors"
                data-testid="bundle-download-all-zip-btn"
              >
                <Download className="h-3.5 w-3.5" />
                Alle Dokumente als ZIP
              </a>
            )}
          </div>

          <div className="p-2 border-t border-zinc-200 bg-zinc-50/50 shrink-0">
            <button
              type="button"
              onClick={exportBundle}
              disabled={bundling}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[12px] font-semibold text-white brand-gradient-bg rounded-md hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-wait shadow-sm"
              data-testid="bundle-export-btn"
            >
              {bundling ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {exportProgress ? `${exportProgress}` : "bündele…"}
                </>
              ) : (
                <>
                  <Package className="h-3.5 w-3.5" />
                  Bundle als PDF
                </>
              )}
            </button>
            <div className="mt-1 text-[10px] text-zinc-500 text-center">
              {(() => {
                let count = bundleSelection.gutachten ? 1 : 0;
                count += Object.values(bundleSelection.docs).filter(Boolean).length;
                return `${count} Dokument${count === 1 ? "" : "e"} ausgewählt`;
              })()}
            </div>
          </div>
        </aside>

        {/* ─────────────── A4-Vorschau ─────────────── */}
        <div
          className="doc-canvas flex-1 overflow-x-auto py-6"
          style={{ background: "#E7EAEE" }}
        >
        {/* ============= SEITE 1 — Titelblatt (GDV-Stil) ============= */}
        <A4Page pageNo={1} totalPages={TOTAL_PAGES} doc={doc}>
          {/* 2-Spalten-Block: Auftraggeber links, Sachverständigenbüro rechts (rechtsbündig) */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16mm", marginTop: "4mm" }}>
            <div>
              <div style={{
                fontSize: "8pt", fontWeight: 700, color: COLORS.accent,
                letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "2mm",
              }}>Auftraggeber</div>
              <div style={{ lineHeight: 1.55, fontSize: "10pt", color: COLORS.body }}>
                <div style={{ color: COLORS.mute, fontSize: "9pt" }}>Herrn / Frau</div>
                <div style={{ fontWeight: 700, fontSize: "11pt", color: COLORS.ink }}>{dash([customer.firstname, customer.name].filter(Boolean).join(" "))}</div>
                <div>{dash(customer.address_line || customer.address || customer.street)}</div>
                <div>{[customer.postal_code, customer.city].filter(Boolean).join(" ") || "—"}</div>
                <div>{customer.country || "Deutschland"}</div>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{
                fontSize: "8pt", fontWeight: 700, color: COLORS.accent,
                letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "2mm",
              }}>Sachverständigenbüro</div>
              <div style={{ lineHeight: 1.55, fontSize: "10pt", color: COLORS.body }}>
                <div style={{ fontWeight: 700, fontSize: "11pt", color: COLORS.ink }}>{dash(doc.gutachterCompany || doc.gutachterName)}</div>
                <div>z.Hd. {dash(doc.gutachterName)}</div>
                <div>{dash(c.owner?.address_line)}</div>
                <div>{[c.owner?.postal_code, c.owner?.city].filter(Boolean).join(" ") || "—"}</div>
                {(c.owner?.phone || c.owner?.mobile) && (
                  <div style={{ marginTop: "2mm", color: COLORS.mute, fontSize: "9pt" }}>
                    {c.owner?.phone && <>TEL {c.owner.phone}<br /></>}
                    {c.owner?.mobile && <>MOBIL {c.owner.mobile}</>}
                  </div>
                )}
                {!doc.gutachterName && !doc.gutachterCompany && (
                  <div
                    data-testid="buero-data-missing-hint"
                    style={{
                      marginTop: "3mm", padding: "2.5mm 3mm", background: "#FEF3C7",
                      border: "1px solid #F59E0B", borderRadius: "4px",
                      fontSize: "8pt", color: "#92400E", textAlign: "left", maxWidth: "70mm",
                      marginLeft: "auto",
                    }}
                  >
                    Keine Büro-Stammdaten gefunden. Bitte im Profil des
                    Akten-Inhabers Name, Firma und Adresse hinterlegen
                    (Einstellungen → Profil). Dieser Hinweis erscheint nicht im PDF.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Title-Block — Eyebrow + großer H1 fast-schwarz, dazu Datumzeile */}
          <div style={{ marginTop: "28mm" }}>
            <div style={{
              fontSize: "9pt", fontWeight: 700, color: COLORS.accent,
              letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: "3mm",
            }}>Schadengutachten unter Haftpflichtgesichtspunkten</div>
            <h1 style={{
              fontSize: "28pt", fontWeight: 700, color: COLORS.ink,
              margin: 0, lineHeight: 1.15, letterSpacing: "-0.01em",
            }}>Haftpflichtgutachten</h1>
            <div style={{ marginTop: "3mm", fontSize: "9pt", color: COLORS.mute }}>
              {[c.owner?.city, docDateLong].filter(Boolean).join(", ")}
            </div>
          </div>

          {/* Stammdaten-KV-Tabelle (GDV-Style mit grauem Label-Background) */}
          <div style={{ marginTop: "14mm" }}>
            <KVList rows={[
              ["Auftragsdatum", orderDateLong],
              ["Dokumentdatum", docDateLong],
              ["Auftraggeber", `${[customer.firstname, customer.name].filter(Boolean).join(" ") || "—"}\n${[customer.address_line || customer.address || customer.street, customer.postal_code, customer.city].filter(Boolean).join(", ") || ""}`],
              ["Amtl. Kennzeichen", vehicle.license_plate],
              ["Versicherung", (() => {
                const name = insurance.insurer_name || insurance.insurance_name;
                if (!name) return "—";
                const addr = insurance.insurer_address || insurance.insurance_address;
                const pc = insurance.insurer_postal_code || insurance.insurance_postal_code;
                const city = insurance.insurer_city || insurance.insurance_city;
                const second = [addr, [pc, city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
                return second ? `${name}\n${second}` : name;
              })()],
              ["Schadennummer", insurance.claim_number || null],
              ["Schadentag", fmtDateLong(insurance.claim_date || c.accident_date)],
              ["Besichtigung", inspDateRaw ? `${inspectionDate}${inspLocation ? "\n" + inspLocation : ""}${inspInspector ? "\nbesichtigt durch " + inspInspector : ""}` : "—"],
              ["Aktenzeichen", doc.caseRef],
            ]} />
          </div>

          {/* Die Bewertungs-Kacheln stehen 1:1 in Kapitel 1 (Zusammenfassung).
              Auf der Titelseite sprengten sie bei gefüllter Wertermittlung die
              A4-Höhe → kaputter Seitenschnitt. Deshalb hier bewusst entfernt. */}
        </A4Page>

        {/* ============= SEITE 2 — Zusammenfassung und Ergebnis ============= */}
        <A4Page pageNo={2} totalPages={TOTAL_PAGES} doc={doc}>
          <SectionTitle eyebrow="Kapitel 1">Zusammenfassung und Ergebnis</SectionTitle>

          <div style={{ marginTop: "3mm" }}>
            <SummaryRow label="Reparaturkosten (ohne MwSt.)" value={fmtEUR(repairCostsNet)} />
            <SummaryRow label="Reparaturkosten (mit MwSt.)" value={fmtEUR(repairCostsGross)} accent />
            <SummaryRow label="Merkantile Wertminderung" value={minderwert > 0 ? fmtEUR(minderwert) : "keine"} />
            <SummaryRow label="Wiederbeschaffungswert" value={fmtEUR(wbwGross)} accent />
            <SummaryRow label="Restwert" value={restwert > 0 ? fmtEUR(restwert) : "nicht notwendig"} />
            <SummaryRow label="Wiederbeschaffungsaufwand (WBW − Restwert)" value={fmtEUR(Math.max(wbwGross - restwert, 0))} />
            <SummaryRow label="Reparaturbeurteilung" value={valuation.reparaturbeurteilung || "—"} />
            <SummaryRow label="Wirtschaftlichkeit" value={valuation.economic_decision ? ({
              wirtschaftlich: "Reparatur wirtschaftlich",
              regel_130: "130-%-Regel — Reparatur zulässig",
              totalschaden: "Wirtschaftlicher Totalschaden",
            }[valuation.economic_decision] || valuation.economic_decision) : "—"} accent={valuation.economic_decision === "totalschaden"} />
            <SummaryRow label="Reparaturfreigabe" value={valuation.reparaturfreigabe || "—"} />
            <SummaryRow label="Reparaturdauer" value={`${repairDays} Arbeitstag(e)`} />
            <SummaryRow label="Wiederbeschaffungsdauer" value={`${recoveryDays} Kalendertag(e)`} />
            <SummaryRow label="Mietwagenklasse" value={`Gruppe ${rentalGroup}`} />
            <SummaryRow label="Fahrbereitschaft" value={inspection.driveable_state || "verkehrssicher"} />
          </div>
        </A4Page>

        {/* ============= SEITE 3 — Fahrzeugbeschreibung ============= */}
        <A4Page pageNo={3} totalPages={TOTAL_PAGES} doc={doc}>
          <SectionTitle eyebrow="Kapitel 2">Fahrzeugbeschreibung</SectionTitle>
          <SubTitle>Allgemeine Fahrzeugdaten</SubTitle>
          <KVList rows={[
            ["Amtl. Kennzeichen", vehicle.license_plate],
            ["Fahrzeug-Identifizierungsnr.", vehicle.vin],
            ["HSN / TSN", vehicle.hsn && vehicle.tsn ? `${vehicle.hsn} / ${vehicle.tsn}` : null],
            ["Farbe", vehicle.color],
            ["Erstzulassung", fmtDateLong(vehicle.first_registration)],
            ["Zugelassen in", vehicle.registered_country || "Deutschland"],
            ["Letzte Zulassung", fmtDateLong(vehicle.last_registration)],
            ["Laufleistung abgelesen", fmtKM(vehicle.mileage)],
          ]} />

          <SubTitle>Technische Fahrzeugdaten</SubTitle>
          <KVList rows={[
            ["Fahrzeugart", vehicle.vehicle_type || "Pkw, SUV, Kleintransporter"],
            ["Hersteller", vehicle.make],
            ["Haupttyp", vehicle.model],
            ["Untertyp", vehicle.sub_model],
            ["Aufbau", vehicle.body_type],
            ["Getriebe", vehicle.gearbox],
            ["Motor", vehicle.engine_description || vehicle.engine_name],
            ["Kraftstoff", vehicle.fuel_type],
            ["Leistung", vehicle.power_kw ? `${vehicle.power_kw} kW` : null],
            ["Hubraum", vehicle.displacement_ccm ? `${vehicle.displacement_ccm} ccm` : null],
            ["Schadstoffklasse", vehicle.emission_class],
          ]} />
        </A4Page>

        {/* ============= SEITE 4 — Auftrag & Versicherung ============= */}
        <A4Page pageNo={4} totalPages={TOTAL_PAGES} doc={doc}>
          <SectionTitle eyebrow="Kapitel 3">Auftrag</SectionTitle>
          {ed("auftrag_text")}

          <SectionTitle eyebrow="Kapitel 4">Versicherung</SectionTitle>
          <KVList rows={[
            ["Schadenart", ({ haftpflicht: "Haftpflichtschaden", kasko: "Kaskoschaden" }[insurance.damage_type] || insurance.damage_type)],
            ["Versicherer (Halter)", insurance.insurer_name || insurance.insurance_name],
            ["Versicherungsnummer (Halter)", insurance.policy_number],
            ["Deckungsart", ({
              haftpflicht: "Haftpflicht", vollkasko: "Vollkasko", teilkasko: "Teilkasko",
            }[insurance.coverage_type] || insurance.coverage_type)],
            ["Versicherer (Unfallgegner)", insurance.opponent_insurer],
            ["Unfallgegner", customer.opponent_name],
            ["Kennzeichen Unfallgegner", customer.opponent_plate],
            ["Schadennummer", insurance.claim_number],
            ["Anschrift Versicherung", [
              insurance.insurer_address || insurance.insurance_address,
              [insurance.insurer_postal_code || insurance.insurance_postal_code,
               insurance.insurer_city || insurance.insurance_city].filter(Boolean).join(" "),
            ].filter(Boolean).join(", ")],
            ["Sachbearbeiter", insurance.adjuster_name || insurance.case_worker],
            ["Telefon Sachbearbeiter", insurance.adjuster_phone],
            ["E-Mail Sachbearbeiter", insurance.adjuster_email],
            ["Schadendatum", fmtDateLong(c.accident_date || insurance.claim_date)],
            ["Unfallort", insurance.damage_location || insurance.accident_location],
            ["Weitere Beteiligte", Array.isArray(insurance.additional_parties)
              ? ((insurance.additional_parties)
                  .map((p) => [
                    p?.name,
                    PARTY_ROLE_LABELS[p?.party_role] || p?.party_role,
                    p?.contact,
                  ].filter(Boolean).join(" · "))
                  .filter(Boolean).join("; ") || null)
              : (typeof insurance.additional_parties === "string"
                  ? insurance.additional_parties : null)],
            ["Selbstbeteiligung", insurance.deductible ? fmtEUR(insurance.deductible) : null],
            ["Vorsteuerabzug berechtigt",
              (insurance.vat_deductible === true || customer.is_taxable === true) ? "Ja"
                : (insurance.vat_deductible === false || customer.is_taxable === false) ? "Nein" : null],
          ]} />
        </A4Page>

        {/* ============= SEITE 5 — Besichtigung & Zustand ============= */}
        <A4Page pageNo={5} totalPages={TOTAL_PAGES} doc={doc}>
          <SectionTitle eyebrow="Kapitel 5">Besichtigung</SectionTitle>
          <KVList rows={[
            ["Datum", inspectionDate],
            ["Adresse", inspLocation],
            ["Besichtigt durch", inspInspector],
            ["Entfernung (einfach)", inspection.distance_km ? `${inspection.distance_km} km` : null],
          ]} />
          <div style={{ marginTop: "4mm" }}>
            {ed("besichtigung_text")}
          </div>

          <SectionTitle eyebrow="Kapitel 6">Zustand</SectionTitle>
          {ed("zustand_text")}
          <KVList rows={[
            ["Allgemeinzustand", beg.allgemeinzustand],
            ["Karosserie", beg.karosseriezustand],
            ["Lack", beg.lackzustand],
            ["Innenraum", beg.innenraumzustand],
          ]} />
        </A4Page>

        {/* ============= SEITE 6 — Schadenhergang & Schadenfeststellung ============= */}
        <A4Page pageNo={6} totalPages={TOTAL_PAGES} doc={doc}>
          <SectionTitle eyebrow="Kapitel 7">Schadenhergang</SectionTitle>
          {ed("schadenhergang_text")}
          {beg.unfallprotokoll && (
            <>
              <SubTitle>Unfallprotokoll</SubTitle>
              <div style={{ whiteSpace: "pre-wrap", fontSize: "10pt", color: COLORS.body }}>
                {beg.unfallprotokoll}
              </div>
            </>
          )}

          <SectionTitle eyebrow="Kapitel 8">Schadenfeststellung</SectionTitle>
          <SubTitle>Schadenbeschreibung</SubTitle>
          <div style={{ whiteSpace: "pre-wrap", fontSize: "10pt", color: COLORS.body }}>
            {dash(c.damage_description) !== "—" ? c.damage_description : (
              <em style={{ color: COLORS.mute }}>Keine Schadenbeschreibung erfasst.</em>
            )}
          </div>

          {/* Auto-Liste aller in der Schadengrafik markierten Schäden */}
          <DamageSelectionSummary
            preDamages={c.pre_damages || {}}
            arrows={c.pre_damages_arrows || []}
          />

          <SubTitle>Weitere Feststellungen</SubTitle>
          {ed("schadenfeststellung_text")}
          <KVList rows={[
            ["Kollisionsart", beg.kollisionsart],
            ["Kompatibilität", beg.kompatibilitaet],
          ]} />

          <SubTitle>Plausibilität</SubTitle>
          {ed("plausibilitaet_text")}

          <SubTitle>Reparierte Vorschäden</SubTitle>
          {ed("vorschaeden_text")}
        </A4Page>

        {/* ============= SEITE 7 — Schadenbilanz (Vorschäden-Diagramm) ============= */}
        <A4Page pageNo={7} totalPages={TOTAL_PAGES} doc={doc}>
          <SectionTitle eyebrow="Anhang">Schadenbilanz</SectionTitle>
          <div style={{ fontSize: "9.5pt", color: COLORS.body, marginBottom: "6mm" }}>
            Übersicht aller am Fahrzeug festgestellten Schäden — visualisiert auf dem schematischen Karosserie-Diagramm. Die Listen rechts unterteilen nach Neu-, Vor- und Altschäden.
          </div>

          {(() => {
            // Multi-Status-Support: ein Teil kann gleichzeitig in mehreren
            // Kategorien sein (z.B. statuses: ["neu","alt"]). Wir splitten
            // ein Teil in mehrere Eintrag-Objekte — eines pro Status —
            // damit die Listen unten korrekt befüllt werden.
            const partEntries = Object.entries(c.pre_damages || {})
              .flatMap(([id, v]) => {
                if (!v) return [];
                const statuses = Array.isArray(v.statuses) && v.statuses.length
                  ? v.statuses
                  : (v.status ? [v.status] : []);
                if (statuses.length === 0) return [];
                const part = DAMAGE_PARTS.find((p) => p.id === id) || (v.label ? { id, label: v.label } : null);
                if (!part) return [];
                return statuses.map((s) => ({ ...part, status: s, note: v.note || "" }));
              });
            const arrowList = (c.pre_damages_arrows || []).map((a, i) => ({
              n: i + 1,
              label: a.label || `Marker ${i + 1}`,
              note: a.note,
              status: a.status,
            }));
            const all = [...partEntries, ...arrowList];
            const neuItems = all.filter((p) => p.status === "neu");
            const vorItems = all.filter((p) => p.status === "vor");
            const altItems = all.filter((p) => p.status === "alt");
            const hasAny = all.length > 0;

            return (
              <div style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 90mm) 1fr",
                gap: "8mm",
                alignItems: "start",
              }}>
                {/* Linke Spalte — Diagramm */}
                <div>
                  <DamageDiagramReadOnly
                    preDamages={c.pre_damages || {}}
                    arrows={c.pre_damages_arrows || []}
                  />
                </div>

                {/* Rechte Spalte — Listen */}
                <div>
                  {hasAny ? (
                    <>
                      <DamageList title="Neuschäden" items={neuItems} color={DAMAGE_STATUS.neu.bg} />
                      <DamageList title="Vorschäden" items={vorItems} color={DAMAGE_STATUS.vor.bg} />
                      <DamageList title="Altschäden" items={altItems} color={DAMAGE_STATUS.alt.bg} />
                    </>
                  ) : (
                    <div style={{
                      padding: "5mm", background: COLORS.light,
                      fontSize: "9.5pt", color: COLORS.mute, textAlign: "center",
                    }}>
                      Keine Schäden am Fahrzeug erfasst.
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </A4Page>

        {/* ============= SEITE 8 — Instandsetzung + Detail-Positionen ============= */}
        <A4Page pageNo={8} totalPages={TOTAL_PAGES} doc={doc}>
          <SectionTitle eyebrow="Kapitel 9">Instandsetzung</SectionTitle>
          <SubTitle>Reparaturauftrag</SubTitle>
          {ed("reparaturauftrag_text")}

          <SubTitle>Reparaturkosten</SubTitle>
          {ed("reparaturkosten_intro_text")}

          <CostsTable
            rows={[
              ["Lackierkosten (ohne MwSt.)", <EditableAmount key="p" value={paintNet} testId="k9-paint-net" onSave={(v) => saveSummaryField("paint_net", v)} />],
              ["Ersatzteilkosten (ohne MwSt.)", <EditableAmount key="e" value={partsNet} testId="k9-parts-net" onSave={(v) => saveSummaryField("parts_net", v)} />],
              ["Arbeitslohn (ohne MwSt.)", <EditableAmount key="l" value={laborNet} testId="k9-labor-net" onSave={(v) => saveSummaryField("labor_net", v)} />],
              ["Reparaturkosten (ohne MwSt.)", <EditableAmount key="n" value={repairCostsNet} testId="k9-subtotal-net" onSave={(v) => saveSummaryField("subtotal_net", v)} />],
              [`${summary.tax_rate || 19}% MwSt.`, fmtEUR(summary.tax_amount || (repairCostsGross - repairCostsNet))],
            ]}
            totalLabel="Reparaturkosten (mit MwSt.)"
            totalValue={fmtEUR(repairCostsGross)}
          />

          {/* Detail-Positionen — nur wenn manuell erfasst (items[]). DAT-Pull-
              back-Positionen liegen im angehängten DAT-PDF, daher hier nicht
              dupliziert. */}
          <PositionsTable title="Arbeitslohn" items={itemsByCategory.labor} />
          <PositionsTable title="Ersatzteile" items={itemsByCategory.parts} />
          <PositionsTable title="Lackierung" items={itemsByCategory.paint} />

          <SubTitle>Merkantile Wertminderung & Wertverbesserung</SubTitle>
          {ed("wertveraenderung_text")}
        </A4Page>

        {/* ============= SEITE 9 — Wertermittlung (WBW, Restwert, Minderwert) ============= */}
        <A4Page pageNo={9} totalPages={TOTAL_PAGES} doc={doc}>
          <SectionTitle eyebrow="Kapitel 10">Wertermittlung</SectionTitle>
          <SubTitle>Wiederbeschaffungswert</SubTitle>
          {ed("wiederbeschaffungswert_text")}

          <CostsTable
            rows={[
              ["Wiederbeschaffungswert (ohne MwSt.)", fmtEUR((wbwGross || 0) / 1.024)],
              ["2,4% MwSt.", fmtEUR(wbwGross ? wbwGross - wbwGross / 1.024 : 0)],
            ]}
            totalLabel="Wiederbeschaffungswert (mit MwSt.)"
            totalValue={fmtEUR(wbwGross)}
          />

          <SubTitle>Restwert</SubTitle>
          {ed("restwert_text")}

          <SubTitle>Merkantiler Minderwert</SubTitle>
          <div style={{ marginTop: "2mm" }}>
            <SummaryRow label="Merkantiler Minderwert" value={minderwert > 0 ? fmtEUR(minderwert) : "keine"} accent={minderwert > 0} />
            {(wbwGross - restwert) > 0 && (
              <SummaryRow label="Entschädigungsbetrag (WBW − Restwert)" value={fmtEUR(wbwGross - restwert)} />
            )}
          </div>
        </A4Page>

        {/* ============= SEITE 10 — Wertermittlung: Wirtschaftlichkeit & Reparaturdauer ============= */}
        <A4Page pageNo={10} totalPages={TOTAL_PAGES} doc={doc}>
          <SectionTitle eyebrow="Kapitel 10 · Fortsetzung">Wirtschaftlichkeit &amp; Reparaturdauer</SectionTitle>
          <SubTitle>Wirtschaftlichkeitsbetrachtung</SubTitle>
          <KVList rows={[
            ["Reparaturbeurteilung", valuation.reparaturbeurteilung],
            ["Wirtschaftliche Entscheidung", valuation.economic_decision ? ({
              wirtschaftlich: "Reparatur wirtschaftlich",
              regel_130: "130-%-Regel — Reparatur zulässig",
              totalschaden: "Wirtschaftlicher Totalschaden",
            }[valuation.economic_decision] || valuation.economic_decision) : null],
            ["Reparaturfreigabe", valuation.reparaturfreigabe],
            ["Wiederbeschaffungsdauer", valuation.wiederbeschaffungsdauer ? `${valuation.wiederbeschaffungsdauer} Kalendertag(e)` : null],
          ]} />

          <SubTitle>Verkehrssicherheit & Reparaturdauer</SubTitle>
          {ed("fahrzeugausfall_text")}
          <div style={{ marginTop: "3mm" }}>
            {ed("reparaturdauer_text")}
          </div>

          {valuation.note && (
            <>
              <SubTitle>Anmerkungen</SubTitle>
              <div style={{ whiteSpace: "pre-wrap", color: COLORS.body, fontSize: "10pt" }}>{valuation.note}</div>
            </>
          )}
        </A4Page>

        {/* ============= SEITE 11 — Nutzungsausfall ============= */}
        <A4Page pageNo={11} totalPages={TOTAL_PAGES} doc={doc}>
          <SectionTitle eyebrow="Kapitel 11">Nutzungsausfall</SectionTitle>
          {ed("nutzungsausfall_text")}

          <div style={{ marginTop: "4mm" }}>
            <SummaryRow label="Nutzungsausfallgruppe" value={rentalGroup} />
            <SummaryRow label="Nutzungsausfallentschädigung" value={`${fmtEUR(dailyRate)} / Tag`} accent />
            {naDays > 0 && (
              <SummaryRow label="Reparaturdauer" value={`${naDays} Arbeitstag(e)`} />
            )}
            {naTotal > 0 && (
              <SummaryRow label="Gesamt-Nutzungsausfall" value={fmtEUR(naTotal)} accent />
            )}
            {rentalTotal > 0 && (
              <SummaryRow
                label={`Alternativ Mietwagen${ausfall.rental_days_count ? ` (${ausfall.rental_days_count} Tag(e) × ${fmtEUR(ausfall.rental_daily_rate || 0)})` : ""}`}
                value={fmtEUR(rentalTotal)}
              />
            )}
            {ausfall.towing_cost > 0 && (
              <SummaryRow label="Bergungs-/Abschleppkosten" value={fmtEUR(ausfall.towing_cost)} />
            )}
          </div>
        </A4Page>

        {/* ============= SEITE 12 — Sachverständigen-Honorar ============= */}
        <A4Page pageNo={12} totalPages={TOTAL_PAGES} doc={doc}>
          <SectionTitle eyebrow="Kapitel 12">Sachverständigen-Honorar</SectionTitle>
          <div style={{ fontSize: "9.5pt", color: COLORS.body, marginBottom: "3mm" }}>
            Grundhonorar nach BVSK-Honorartabelle, orientiert an der Schadenhöhe brutto. Nebenkosten gem. § 7 BVSK-Empfehlung.
          </div>

          <CostsTable
            rows={[
              ["Grundhonorar (BVSK)", fmtEUR(honorarBase)],
              ...(honorarNkItems.length > 0
                ? honorarNkItems.map((n) => [n.label || n.key || "Nebenkosten", fmtEUR(Number(n.amount) || 0)])
                : [
                    [`Fahrtkosten (${honorarTravelKm.toLocaleString("de-DE", { maximumFractionDigits: 1 })} km × ${honorarTravelRate.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €/km)`, fmtEUR(honorarTravelTotal)],
                    ["Foto-Pauschale", fmtEUR(honorarPhoto)],
                    ["Schreibkosten", fmtEUR(honorarWriting)],
                    ["Porto / Telekom-Pauschale", fmtEUR(honorarPostage)],
                  ]),
              ["Honorar Nettobetrag", fmtEUR(honorarSubtotal)],
              [`${honorarTaxRate}% Umsatzsteuer`, fmtEUR(honorarTax)],
            ]}
            totalLabel="Honorar inkl. MwSt."
            totalValue={fmtEUR(honorarGross)}
          />

          {honorar.liability_percentage > 0 && honorar.liability_percentage < 100 && (
            <div style={{ marginTop: "4mm" }}>
              <SummaryRow label={`Haftungsquote ${honorar.liability_percentage}%`} value={fmtEUR(honorarGross * honorar.liability_percentage / 100)} accent />
            </div>
          )}

          {honorar.note && (
            <div style={{ marginTop: "4mm", fontSize: "9.5pt", color: COLORS.body, whiteSpace: "pre-wrap" }}>
              {honorar.note}
            </div>
          )}

          {/* Bankverbindung des Sachverständigen — für die Honorar-Überweisung */}
          {(doc.iban || doc.bankName) && (
            <div
              style={{
                marginTop: "8mm",
                padding: "4mm 5mm",
                background: COLORS.light,
                fontSize: "9.5pt",
                color: COLORS.body,
                lineHeight: 1.7,
              }}
              data-testid="doc-honorar-bank-block"
            >
              <div style={{
                fontSize: "8pt", fontWeight: 700, color: COLORS.accent,
                letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "1.5mm",
              }}>
                Bankverbindung
              </div>
              <div>
                Kontoinhaber: <strong>{doc.gutachterCompany || doc.gutachterName}</strong>
                {doc.bankName && <> · Bank: <strong>{doc.bankName}</strong></>}
              </div>
              <div>
                {doc.iban && <>IBAN: <strong style={{ fontFamily: "monospace" }}>{doc.iban}</strong></>}
                {doc.iban && doc.bic && " · "}
                {doc.bic && <>BIC: <strong style={{ fontFamily: "monospace" }}>{doc.bic}</strong></>}
              </div>
            </div>
          )}
        </A4Page>

        {/* ============= SEITE 13 — Schlusswort + Unterschrift ============= */}
        <A4Page pageNo={13} totalPages={TOTAL_PAGES} doc={doc}>
          <SectionTitle eyebrow="Kapitel 13">Schlusswort</SectionTitle>
          {ed("schlusswort_text")}

          <div style={{ marginTop: "24mm" }}>
            <div style={{ fontSize: "9pt", color: COLORS.mute, letterSpacing: "0.05em", textTransform: "uppercase" }}>Mit freundlichen Grüßen</div>
            {c.owner?.signature_data_url && (
              <img
                src={c.owner.signature_data_url}
                alt="Unterschrift Sachverständiger"
                style={{ height: "16mm", marginTop: "8mm", display: "block", objectFit: "contain" }}
                data-testid="doc-expert-signature"
              />
            )}
            <div style={{ fontSize: "12pt", fontWeight: 700, color: COLORS.ink, marginTop: c.owner?.signature_data_url ? "2mm" : "12mm" }}>
              {dash(doc.gutachterName)}
            </div>
            {doc.gutachterCompany && (
              <div style={{ fontSize: "9.5pt", color: COLORS.body, marginTop: "1mm" }}>{doc.gutachterCompany}</div>
            )}
          </div>
        </A4Page>

        {/* ============= SEITE 9 — Abtretungserklärung (Anlage) ============= */}
        <AbtretungsErklaerung
          c={c}
          user={c.owner}
          accessToken={accessToken}
          gutachterName={c.owner?.name || c.owner_name || ""}
          pageNo={ABTRETUNG_PAGE}
          totalPages={TOTAL_PAGES}
          onClauseChange={async (next) => {
            try {
              await api.patch(`/cases/${c.id}`, {
                abtretung: { ...(c.abtretung || {}), clauses: next },
              });
              onSaved?.();
            } catch (e) {
              toast.error(apiError(e, "Speichern der Klauseln fehlgeschlagen"));
            }
          }}
        />

        {/* ============= LICHTBILDER (dynamische Anzahl) ============= */}
        {photos.length > 0 && Array.from({ length: photoPages }).map((_, pageIdx) => {
          const start = pageIdx * PHOTOS_PER_PAGE;
          const pagePhotos = photos.slice(start, start + PHOTOS_PER_PAGE);
          const pageNo = FIRST_PHOTO_PAGE + pageIdx;
          return (
            <A4Page key={`photo-page-${pageIdx}`} pageNo={pageNo} totalPages={TOTAL_PAGES} doc={doc}>
              {pageIdx === 0 && (
                <SectionTitle eyebrow="Anhang">Lichtbilder</SectionTitle>
              )}
              {pageIdx > 0 && (
                <div style={{
                  fontSize: "8pt", fontWeight: 700, color: COLORS.mute,
                  letterSpacing: "0.1em", textTransform: "uppercase",
                  marginTop: "2mm", marginBottom: "5mm",
                }}>Lichtbilder · Fortsetzung</div>
              )}
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "6mm 6mm",
                marginTop: pageIdx === 0 ? "3mm" : 0,
              }}>
                {pagePhotos.map((p, i) => (
                  <div key={p.id || `${pageIdx}-${i}`} style={{ breakInside: "avoid" }}>
                    <div style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      background: COLORS.light,
                      overflow: "hidden",
                      border: `0.5pt solid ${COLORS.border}`,
                    }}>
                      <img
                        src={photoUrl(p.storage_path, accessToken)}
                        alt={photoLabel(p, start + i)}
                        crossOrigin="anonymous"
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    </div>
                    <div style={{
                      fontSize: "8.5pt", color: COLORS.body,
                      marginTop: "1.5mm", textAlign: "center",
                      lineHeight: 1.4,
                    }}>
                      <span style={{
                        fontWeight: 700, color: COLORS.accent,
                        marginRight: "1.5mm",
                      }}>Bild {start + i + 1}</span>
                      {photoLabel(p, start + i)}
                    </div>
                  </div>
                ))}
              </div>
            </A4Page>
          );
        })}

        {/* ============= ANLAGEN (letzte Seite, dynamisch) ============= */}
        <A4Page pageNo={APPENDIX_PAGE} totalPages={TOTAL_PAGES} doc={doc}>
          <SectionTitle eyebrow="Kapitel 14">Anlagen</SectionTitle>
          <ul style={{ fontSize: "10pt", lineHeight: 1.9, marginLeft: "6mm", color: COLORS.body }}>
            <li>Schadenkalkulation vom {kalkulationDate}</li>
            <li>Lichtbilder ({photos.length} Aufnahmen)</li>
            {c.documents?.length > 0 && <li>Weitere Dokumente ({c.documents.length})</li>}
            {valuation.replacement_value > 0 && <li>SilverDAT Gebrauchtfahrzeug-Bewertung</li>}
          </ul>

          {/* Angehängte DAT-PDFs als klickbare Liste — wird beim PDF-Export
              ausgeblendet (no-print Klasse), erscheint nur in der Live-Ansicht. */}
          {datDocuments.length > 0 && (
            <div
              className="dat-attachments-list no-print"
              style={{
                marginTop: "10mm",
                padding: "4mm 5mm",
                background: "#FFF8E7",
                border: "1pt solid #F0C674",
                borderRadius: "2mm",
              }}
              data-testid="doc-dat-attachments"
            >
              <div style={{ fontSize: "9pt", fontWeight: 700, color: "#7A4F00", marginBottom: "3mm" }}>
                Angehängte DAT-Kalkulationen ({datDocuments.length})
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: "9pt", lineHeight: 1.6 }}>
                {datDocuments.map((doc) => (
                  <li key={doc.id || doc.storage_path} style={{ marginBottom: "2mm" }}>
                    <a
                      href={buildDocUrl(doc.storage_path)}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        color: "#7A4F00",
                        textDecoration: "none",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "1.5mm",
                        fontWeight: 600,
                      }}
                    >
                      <FileText size={12} />
                      {doc.original_filename || "DAT-Kalkulation.pdf"}
                      <ExternalLink size={10} style={{ opacity: 0.6 }} />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ marginTop: "16mm", padding: "5mm 6mm", background: COLORS.light, fontSize: "8.5pt", color: COLORS.mute, lineHeight: 1.6 }}>
            <span style={{ color: COLORS.ink, fontWeight: 700 }}>Hinweis</span>{" — "}
            Die Anhänge (Schadenkalkulation, Lichtbilder, Marktanalyse, Rechnung) werden beim PDF-Export
            automatisch angehängt. Textblöcke im Gutachten sind direkt anklickbar und editierbar — alles
            wird automatisch gespeichert.
          </div>
        </A4Page>
        </div>
      </div>

      <style>{`
        @media print {
          .doc-canvas { background: white !important; }
          .doc-a4 { box-shadow: none !important; margin-bottom: 0 !important; page-break-after: always; }
          [contenteditable] { caret-color: transparent; }
          .no-print { display: none !important; }
        }
      `}</style>

      {/* DAT-PDF-Scan-Modal — gibt extrahierte Daten zurück und wir patchen
          die Akte. Backend-Endpoint /api/ai/scan-dat-pdf nutzt Gemini-2.5-pro. */}
      <DATPdfScanModal
        caseId={c.id}
        open={datScanOpen}
        initialFile={datScanFile}
        onClose={() => { setDatScanOpen(false); setDatScanFile(null); }}
        onApplied={(patch) => {
          // Parent informieren, dass die Akte sich geändert hat. Der
          // DocumentationStep wird durch das c-Prop-Update automatisch neu
          // gerendert mit den neuen Werten (Fahrzeug-Stammdaten,
          // Kalkulations-Summen, Detail-Positionen).
          onSaved?.(patch);
          toast.success("DAT-Werte in Dokumentation übernommen");
        }}
      />

      {/* DAT-Live-Pull-Dialog — User gibt Vorgangsnummer ein, wir holen
          Live-Daten aus DAT via SOAP. Pull-Back-Endpoint persistiert die
          Werte direkt in die Akte (vehicle, calculation, valuation) — wir
          triggern dann einen Reload damit alle Sektionen der Doku die
          neuen Daten anzeigen. */}
      <DATContractPullDialog
        caseId={c.id}
        open={datPullOpen}
        onClose={() => setDatPullOpen(false)}
        defaultContractId={c.dat_contract_id || ""}
        onPulled={() => {
          // Hartes Reload — DAT-Pull-Back ändert vehicle, calculation und
          // valuation parallel. Statt jedes Feld einzeln zu mergen, lassen
          // wir den Parent die Akte komplett neu laden.
          if (typeof window !== "undefined") {
            window.location.reload();
          }
        }}
      />
    </div>
  );
}
